import { IonIcon } from "@ionic/react";
import { useTranslation } from "react-i18next";
import {
  cardOutline,
  checkmarkCircleOutline,
  closeCircleOutline,
  timeOutline,
} from "ionicons/icons";
import { type EntryState, toEntryState } from "@/lib/shift/entry-status";

/** Which of the two tracks a chip is about. A shift has both, and they move independently. */
export type EntryKind = "hours" | "expenses";

/**
 * The palette has three semantic ramps and the brand colour is white-label, so it cannot carry
 * meaning — a fourth hue would have to be invented, and some brand's theme would turn it into a
 * warning. Each state sits on its light tint with `120` — the same colour, darkened until it is
 * legible — because white on `success-100` lands at about 3.5:1 where AA wants 4.5:1 at this size.
 *
 * There was a fourth chip, `paid`, sharing the success ramp and told apart by a ring and a card
 * icon. It went with the `paid` state itself — see `entry-status.ts`. The ring-instead-of-inversion
 * trick is worth remembering if payroll state ever reaches the app again.
 */
const TONE: Record<EntryState, string> = {
  pending: "bg-warning-10 text-warning-120",
  rejected: "bg-error-10 text-error-120",
  approved: "bg-success-10 text-success-120",
};

const STATE_ICON: Record<EntryState, string> = {
  pending: timeOutline,
  rejected: closeCircleOutline,
  approved: checkmarkCircleOutline,
};

type EntryStatusChipProps = {
  kind: EntryKind;
  /** The raw status off the api. Unknown or absent renders nothing. */
  status: string | null | undefined;
  onClick?: (event: React.MouseEvent) => void;
};

/**
 * One status, in the employee's words.
 *
 * The single place a status becomes a label, a colour and an icon — the row, the hours sheet and
 * the declaration sheet all render this. Before, each of those had its own colour switch and its
 * own translation call, which is how the declaration status ended up being conveyed by nothing but
 * the tint of the word "Declaratie".
 */
export function EntryStatusChip({ kind, status, onClick }: EntryStatusChipProps) {
  const { t } = useTranslation();
  const state = toEntryState(status);

  if (!state) {
    return null;
  }

  const label = t(`shift:entry-status.state.${state}`);
  const kindLabel = t(`shift:entry-status.kind.${kind}`);
  const content = (
    <>
      <IonIcon icon={STATE_ICON[state]} className="size-3.5 shrink-0" aria-hidden="true" />
      <span>{label}</span>
    </>
  );

  const className = `inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-semibold leading-[1.5] ${TONE[state]}`;

  // A chip is ~20px tall, well under the 44px a thumb needs. `touch-target` is the repo's own
  // utility for exactly this: a pseudo-element of at least 2.75rem centred on the control, so the
  // tap area grows without the ink or the compact row growing with it.
  const hitArea = "touch-target";

  // The kind is carried by the icon visually, but a screen reader gets it spelled out — "Uren:
  // afgewezen" is unambiguous where a clock glyph is not.
  const accessibleLabel = `${kindLabel}: ${label}`;

  if (onClick) {
    return (
      <button
        type="button"
        onClick={onClick}
        aria-label={accessibleLabel}
        className={`${hitArea} transition-opacity active:opacity-70`}
      >
        <span className={className}>{content}</span>
      </button>
    );
  }

  return (
    <span aria-label={accessibleLabel} className={className}>
      {content}
    </span>
  );
}
